import { useLang } from "../i18n/LanguageContext";
import { GiAllSeeingEye, GiBookmarklet, GiMoon, GiOpenBook, GiQuillInk } from "react-icons/gi";

const COPY = {
  pl: {
    eyebrow: "Proces",
    title: "Jak sen trafia do Morfeusza",
    steps: [
      {
        icon: GiQuillInk,
        title: "Opisz sen",
        desc: "Zapisujesz obrazy, osoby, miejsca i nastrój tak wiernie, jak pamiętasz. To pierwszy ślad w Księdze Snów.",
      },
      {
        icon: GiAllSeeingEye,
        title: "Odczytaj symbole",
        desc: "Morfeusz porządkuje motywy snu: progi, wodę, domy, światło, cienie i emocje, które wracają między snami.",
      },
      {
        icon: GiBookmarklet,
        title: "Zapisz w Księdze",
        desc: "Interpretacja, notatka i rozpoznane symbole mają zostać razem, aby sen nie znikał po jednym odczycie.",
      },
      {
        icon: GiMoon,
        title: "Wracaj do motywów",
        desc: "Z czasem Księga pokazuje powracające znaki, nastroje i wątki, z których powstaje osobisty język snów.",
      },
    ],
  },
  en: {
    eyebrow: "Process",
    title: "How a dream enters Morpheus",
    steps: [
      {
        icon: GiQuillInk,
        title: "Describe the dream",
        desc: "You write down images, people, places and mood as faithfully as you remember. This becomes the first trace in the Dream Codex.",
      },
      {
        icon: GiAllSeeingEye,
        title: "Read the symbols",
        desc: "Morpheus organises motifs: thresholds, water, houses, light, shadows and emotions that return between dreams.",
      },
      {
        icon: GiBookmarklet,
        title: "Save to the Codex",
        desc: "The reading, note and recognised symbols are meant to stay together so the dream does not vanish after one reading.",
      },
      {
        icon: GiMoon,
        title: "Return to patterns",
        desc: "Over time, the Codex reveals recurring signs, moods and threads that become your private dream language.",
      },
    ],
  },
};

export const HowItWorks = () => {
  const { lang } = useLang();
  const copy = COPY[lang] ?? COPY.pl;

  return (
    <section
      id="how"
      className="bg-parchment-dark py-24 md:py-32 border-y border-ink/10"
      data-ui="home-how"
      data-testid="how-it-works"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10" data-ui="home-how-inner">
        <div className="text-center max-w-2xl mx-auto">
          <span className="font-accent text-[11px] uppercase tracking-[0.3em] text-gold-muted">
            {copy.eyebrow}
          </span>
          <h2 className="font-heading italic text-4xl md:text-5xl text-ink mt-4">
            {copy.title}
          </h2>
          <div className="divider-ornate mt-8 max-w-xs mx-auto">●</div>
        </div>

        <div className="mt-16 grid md:grid-cols-4 gap-px bg-ink/10">
          {copy.steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <div
                key={step.title}
                className="bg-parchment-dark p-8 md:p-10 text-center group hover:bg-parchment transition-colors"
                data-ui="home-how-step"
                data-testid={`how-step-${i}`}
              >
                <div className="flex justify-center">
                  <div className="w-16 h-16 rounded-full border border-gold-muted/60 flex items-center justify-center group-hover:border-gold-muted transition-colors">
                    <Icon className="text-2xl text-ink group-hover:text-gold-muted transition-colors" />
                  </div>
                </div>
                <div className="font-accent text-[10px] uppercase tracking-[0.35em] text-gold-muted mt-6">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-heading italic text-2xl md:text-3xl text-ink mt-3">
                  {step.title}
                </h3>
                <p className="font-body text-sm text-ink-soft mt-4 leading-relaxed">
                  {step.desc}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
